import React from "react";
import { useHistory } from "react-router-dom";
import { Button, Typography } from "@material-ui/core";
import DataTable from "@/components/DataTable";
import { ReactRender } from "@/components/ReactRender";
import { useTranslation } from "@/langs/useTranslation";
import { getAlarmDeviceListApi } from "@/apis/apiCollection";
import ChipStatus from "@/components/ChipStatus";
import HasPermission from "@/components/commons/HasPermission";
import { formatDate } from "./datetimeHandlers";
import { PublishChipStatus } from "@/components/FormFields/PublishStatus";
import { renderAlarmPolicy } from "./utils";

export const AlarmDeviceListDatatable = React.forwardRef(
  (
    {
      className,
      style,
      getSearchParams,
      footer,
      children,
      onCheckAll,
      onLoaded,
      onReset,
      onDelete
    },
    ref
  ) => {
    const { t } = useTranslation();
    const history = useHistory();

    const toDetail = e => {
      e.preventDefault();
      const url = new URL(e.target.href);
      history.push(url.pathname);
    };

    const columns = [
      {
        data: row => {
          return `${row.deviceId},${row.alarmTemplateId},${row.alarmPolicyId}`;
        },
        type: "checkbox"
      },
      {
        name: "deviceSerialNumber",
        data: "deviceSerialNumber",
        title: t({ defaultMessage: "设备SN" }),
        createdCell: (td, data, rowData) => {
          ReactRender(
            <HasPermission permission="Device Monitoring Detail">
              {yes =>
                yes ? (
                  <a
                    href={`/device-list/device-detail/${rowData.deviceId}/${
                      rowData.os_type || "android"
                    }`}
                    onClick={toDetail}
                  >
                    {data}
                  </a>
                ) : (
                  data
                )
              }
            </HasPermission>,
            td
          );
        }
      },
      {
        name: "deviceDetail",
        data: "deviceName",
        orderable: false,
        title: t({ defaultMessage: "设备名称" })
      },
      {
        name: "deviceDetail",
        data: "deviceDetail",
        orderable: false,
        title: t({ defaultMessage: "描述" })
      },
      {
        name: "groups",
        orderable: false,
        data: row => {
          return row.groups.map(item => item.name).join(", ");
        },
        title: `${t({ defaultMessage: "门店" })}`
      },
      {
        name: "tags",
        orderable: false,
        data: row => {
          return row.tags.map(item => item.name).join(", ");
        },
        title: t({ defaultMessage: "标签" })
      },
      {
        name: "alarmTemplateName",
        data: "alarmTemplateName",
        orderable: false,
        title: `${t({ defaultMessage: "报警模板名称" })}`,
        render: data => {
          return data
            ? data.replaceAll(">", "&gt;").replaceAll("<", "&lt;")
            : data;
        }
      },
      {
        data: "alarmPolicyOptionName",
        orderable: false,
        title: `${t({ defaultMessage: "报警规则" })}`,
        render: (data, type, row) => {
          return renderAlarmPolicy(data, row);
        }
      },
      {
        data: "publishStatus",
        orderable: false,
        title: t({ defaultMessage: "发布状态" }),
        className: "center",
        createdCell: (td, data) => {
          ReactRender(<PublishChipStatus status={data} />, td);
        }
      },
      {
        data: "publishDate",
        orderable: false,
        title: t({ defaultMessage: "报警发布日期" }),
        searchable: false,
        render: data => {
          return formatDate(data);
        }
      },
      {
        data: "triggerNum",
        orderable: false,
        title: t({ defaultMessage: "触发状态" }),
        className: "center",
        createdCell: (td, data) => {
          ReactRender(
            <ChipStatus
              size="small"
              type={data > 0 ? "info" : "warn"}
              label={
                data > 0
                  ? t({ defaultMessage: "已触发" })
                  : t({ defaultMessage: "未触发" })
              }
            />,
            td
          );
        }
      },
      {
        data: "triggerValue",
        orderable: false,
        title: t({ defaultMessage: "触发值" }),
        className: "center",
        createdCell: (td, data) => {
          ReactRender(
            <Typography variant="body2" color="primary">
              {data}
            </Typography>,
            td
          );
        }
      },
      {
        data: "triggerNum",
        orderable: false,
        title: t({ defaultMessage: "触发次数" }),
        className: "center",
        createdCell: (td, data) => {
          ReactRender(
            <Typography variant="body2" color="primary">
              {data}
            </Typography>,
            td
          );
        }
      },
      {
        data: "triggerDate",
        orderable: false,
        title: t({ defaultMessage: "最后触发时间" }),
        render: data => {
          return formatDate(data);
        }
      },
      {
        data: "createUserName",
        orderable: false,
        className: "center",
        title: t({ defaultMessage: "部署人" })
      },
      {
        data: null,
        title: t({ defaultMessage: "操作" }),
        // className: "operation2",
        searchable: false,
        orderable: false,
        width: 48,
        createdCell: (td, data, row) => {
          ReactRender(
            <HasPermission permission="alarm_list_alarm_reset">
              <Button size="small" color="primary" onClick={() => onReset(row)}>
                {data.alarmPolicyOptionName === "App Crash Detection"
                  ? t({ defaultMessage: "重启" })
                  : t({ defaultMessage: "重置" })}
              </Button>
            </HasPermission>,
            td
          );
        }
      }
      // {
      //   name: "alarmTemplateName",
      //   data: "alarmTemplateName",
      //   className: "sticky right operation",
      //   searchable: false,
      //   orderable: false,
      //   width: 48,
      //   createdCell: (td, data, row) => {
      //     ReactRender(
      //       <HasPermission permission="alarm_list_alarm_enable_disable">
      //         <Button
      //           size="small"
      //           color="secondary"
      //           onClick={() => onDelete(row)}
      //         >
      //           {t({ defaultMessage: "删除" })}
      //         </Button>
      //       </HasPermission>,
      //       td
      //     );
      //   }
      // }
    ];

    const ajax = (data, callback) => {
      let orderType;
      let orderWay;
      const order = data.order[0];
      if (order) {
        // orderType = data.columns[order.column]?.name;
        orderType = order.column;
        orderWay = order.dir === "asc" ? 1 : 2;
      }
      return getAlarmDeviceListApi({
        // ...data,
        orderType,
        orderWay,
        ...getSearchParams(),
        page: data.page,
        pageSize: data.pageSize
      }).then(res => {
        callback({
          data: res.alarmDevices,
          draw: data.draw,
          recordsFiltered: res.total,
          recordsTotal: res.total
        });
      });
    };

    return (
      <DataTable
        ref={ref}
        rowsGroup={[
          "deviceSerialNumber:name",
          "deviceDetail:name",
          "groups:name",
          "tags:name",
          "alarmTemplateName:name"
        ]}
        ordering
        options={{ order: [[1, "asc"]] }}
        columns={columns}
        ajax={ajax}
        className={className}
        style={style}
        footer={footer}
        scrollable
        showCheckAll={false}
        onCheckAll={onCheckAll}
        onLoaded={onLoaded}
      >
        {children}
      </DataTable>
    );
  }
);
